import { graphql } from "@/__generated__";

export const TestLevelBySlugQuery = graphql(`
  query TestLevelBySlugQuery($slug: String!) {
    testLevelBySlug(slug: $slug) {
      ...TestLevel
    }
  }
`);

export const TestLevelQuery = graphql(`
  query TestLevelQuery($id: ID!) {
    testLevel(id: $id) {
      id: testLevelId
      slug
      name
      playlistId
      description
      descriptionEn
    }
  }
`);

export const TestLevelCheckPointsFullQuery = graphql(`
  query CheckPointsFullQuery(
    $first: Int
    $after: Cursor
    $last: Int
    $before: Cursor
    $filterBy: CheckPointFilter
  ) {
    checkPoints: checkPointsFull(
      first: $first
      after: $after
      last: $last
      before: $before
      filterBy: $filterBy
      orderBy: { key: TEST_LEVEL_ORDER }
    ) {
      edges {
        cursor
        node {
          ...TestLevelCheckPoint
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`);

export const TestLevelFragment = graphql(/* GraphQL */ `
  fragment TestLevel on TestLevel {
    id: testLevelId
    slug
    name
    playlistId
    typeTestLevel
    description
    descriptionEn
  }
  fragment UserError on UserError {
    message
    code
    field
    param {
      value
    }
  }
`);

export const TestLevelCheckPointFragment = graphql(/* GraphQL */ `
  fragment TestLevelCheckPoint on CheckPoint {
    id: checkPointId
    name
    nameEn
    meta {
      externalUrl
    }
    viewerCompleted
    isPremium
    partsCount
    testCount
    time
    viewerCanAccess
    viewerIsSubscribing
    speakingParts {
      id
      speakingPartId
      partType
    }
    testLevel {
      id
      name
      slug
    }
  }
`);

export const MoreInfoCheckPointQuery = graphql(/* GraphQL */ `
  query MoreInfoCheckPointQuery($id: ID!) {
    checkPoint(id: $id) {
      id
      viewerCanAccess
      name
      nameEn
      checkPointId
      cpType
      type
      examStructures {
        structureType {
          name
          value
        }
      }
      sectionCheckPoints {
        id
        parentStructureType
        checkPointId
        parentPartOrder
      }
    }
  }
`);
export const RecordScoreAttemptFragment = graphql(`
  fragment RecordScoreAttemptFragment on RecordScore {
    transcript
    suggestions {
      audioId
      id
      text
    }
    vocabularyGrammarScore
    pronunciationScore
    interactionScore
    vocabularyGrammarScorePercent
    vocabularyGrammarDecision
    interactionScorePercent
    pronunciationScore
    interactionScore
    overallScore
    coherence {
      decision
      score
    }
    coherenceScore
    vocabulary {
      answerKeyScore
      score
      decision
      cefrScore
      cefrDistribution {
        cefrLevel
        score
      }
      highlightKeywords {
        startIndex
        endIndex
        keyword
      }
    }
    grammar {
      score
      decision
      highlightItems {
        startIndex
        endIndex
        original
        suggestion
      }
    }
    pronunciation {
      generalScore
      intonationScore
      score
      decision
      highlightWords {
        startIndex
        endIndex
        text
        ipa
        nativenessScore
        decision
        audioUrl
        phonemes {
          startIndex
          endIndex
          text
          ipa
          nativenessScore
          decision
        }
      }
    }
    interaction {
      receptionResponding
      fluencyPromptness
      receptionDecision
      fluencyDecision
      fluency {
        wordsPerMinute
      }
    }
  }
`);
export const IeltsSpeakingQuestionFragment = graphql(/* GraphQL */ `
  fragment IeltsSpeakingQuestion on IeltsSpeakingQuestion {
    id
    title
    sampleAnswerHtml
    sampleAnswerAudio {
      id
      url
      subtitle
    }
    timeToSpeak
    timeToThink
    audio {
      id
      url
    }
    contentHtml
    ieltsSpeakingQuestionId
    name
  }
`);

export const IeltsSpeakingCheckPointsQuery = graphql(`
  query CheckPointIeltsQuery($id: ID!) {
    checkPoint(id: $id) {
      id
      name
      nameEn
      description
      descriptionEn
      diamond
      cpType
      type
      viewerCanAccess
      theme {
        id
        name
        nameEn
        key
      }
      sectionCheckPoints {
        id
        name
        nameEn
        parentStructureType
        checkPointId
        parentPartOrder
        diamond
      }
      checkPointIeltsSpeakingParts {
        node {
          id
          expiredTime
          order
          defaultShowQuestionContents
          info {
            id
            title
            description
            introductionAudio {
              id
              url
            }
            introductionText
          }
          ieltsSpeakingPartQuestions {
            node {
              ...IeltsSpeakingQuestion
            }
          }
        }
      }
    }
  }
`);

export const ExamCheckPointFullFragment = graphql(/* GraphQL */ `
  fragment ExamCheckPointFull on CheckPoint {
    id
    viewerCanAccess
    name
    nameEn
    checkPointId
    cpType
    type
    testLevel {
      ...TestLevel
    }
    examStructures {
      structureType {
        name
        value
      }
      parts {
        order
        node {
          examPartId
          totalMarks
          id
          maxScore
          answerExplanationHTML
          answerExplanationHTMLEn
          partType
          totalMarks
          contents {
            id
            body {
              formattedText {
                text
                format
                label
                metadataText
              }
              images {
                imageId
                label
                position
              }
            }
          }
          questions {
            isExample
            id
            order
            title
            answerExplanationHTML
            answerExplanationHTMLEn
            details {
              id
              labelOrder
              order
              prefix
              prefix
              detail {
                formattedText {
                  format
                  label
                  metadataText
                  text
                }
                textIndexes {
                  text
                  index
                }
                valueType
                box {
                  height
                  left
                  top
                  width
                }
              }
              result {
                isSelected
                value
                formattedValue {
                  format
                  text
                }
              }
            }
            titlePrefix {
              format
              label
              metadataText
              text
            }
            detailPrefix {
              format
              label
              metadataText
              text
            }
            content {
              format
              label
              metadataText
              text
            }
            choices {
              format
              label
              metadataText
              text
            }
          }
          time
          title
          structureType
        }
        miniCheckPointId
      }
      totalMarks
      time
    }
    sectionCheckPoints {
      id
      parentStructureType
      checkPointId
      parentPartOrder
      diamond
    }
  }
`);

export const ExamPartAttemptFragment = graphql(`
  fragment ExamPartAttempt on ExamPartAttempt {
    id
    examPartAttemptId
    score
    maxScore
    part {
      id
      examPartId
      title
      partType
      structureType
      maxScore
      questions {
        id
        title
        order
      }
      time
    }
    position
    questionDetailAttempts {
      id
      examQuestionDetailAttemptId
      answer {
        value
        formattedValue {
          text
          format
          label
          metadataText
        }
        isSelected
        valueType
      }
      isCorrect
      result {
        value
        formattedValue {
          text
          format
          label
          metadataText
        }
        isSelected
        valueType
      }
      questionDetail {
        id
        prefix
      }
    }
  }
`);

export const ExamStructureAttemptsFragment = graphql(`
  fragment ExamStructureAttempts on ExamStructureAttempt {
    totalCorrectMarks
    structureType {
      value
      name
    }
    partAttempts {
      ...ExamPartAttempt
    }
  }
`);

export const GetResultUserAttempt = graphql(`
  query GetResultUserAttempt($id: ID!) {
    userAttempt(id: $id) {
      id
      grade
      maxGrade
      userAttemptId
      assignTest {
        id
        assignTestId
        isHiddenAnswer
        endDate
        allowLateSubmit
        examMode
      }
      totalScore
      totalMaxScore
      executionTime
      createdAt
      learnerProfile {
        id
        displayName
        frameImage {
          id
          url
        }
        avatarUrl
        learnerProfileId
      }
      ieltsListeningPartAttempts {
        id
        ieltsListeningPartAttemptId
        questionAttempts {
          id
          correctMark
          ieltsListeningQuestionAttemptId
          detailAnswers {
            text
            optionIds
            optionId
            detailId
            correctOptionIDs
          }
          question {
            id
            ieltsListeningQuestionId
          }
        }
        part {
          id
        }
      }
      ieltsReadingPartAttempts {
        id
        ieltsReadingPartAttemptId
        questionAttempts {
          id
          correctMark
          ieltsReadingQuestionAttemptId
          detailAnswers {
            text
            optionIds
            optionId
            detailId
            correctOptionIDs
          }
          question {
            id
            ieltsReadingQuestionId
          }
        }
        part {
          id
        }
      }
      checkPoint {
        id
        checkPointId
        name
        nameEn
        time
        type
        parentCheckPoint {
          id
          checkPointId
        }
        checkPointIeltsListeningParts {
          position
          node {
            id
            expiredTime
            audioSubtitle
            audio {
              id
              url
            }
            ieltsListeningPartId
            name
            order
            title
            ieltsListeningPartQuestions {
              ...IeltsListeningQuestion
            }
          }
        }
        checkPointIeltsReadingParts {
          position
          node {
            id
            expiredTime
            contentHtml
            ieltsReadingPartId
            name
            order
            title
            ieltsReadingPartQuestions {
              ...IeltsReadingQuestion
            }
          }
        }
      }
    }
  }
`);

export const TestLevelsWithFilter = graphql(`
  query TestLevelsWithFilter($filterBy: TestLevelFilter) {
    testLevels(first: 50, filterBy: $filterBy) {
      nodes {
        ...TestLevel
      }
    }
  }
`);

export const SubmitExamQuestionDetailAttemptMutation = graphql(`
  mutation SubmitExamQuestionDetailMutation(
    $input: ExamSubmitPartAttemptInput!
  ) {
    examSubmitPartAttempt(input: $input) {
      userErrors {
        ...UserError
      }
    }
  }
`);

export const SignUpViewSignUpMutation = graphql(`
  mutation SignUpViewSignUpMutation($input: ViewerSignUpInput!) {
    viewerSignUp(input: $input) {
      viewer {
        ...Viewer
      }
      userErrors {
        code
        field
        message
        param {
          value
        }
      }
    }
  }
`);
export const IeltsListeningQuestionFragment = graphql(/* GraphQL */ `
  fragment IeltsListeningQuestion on IeltsListeningPartQuestionEdge {
    position
    node {
      id
      ieltsListeningQuestionId
      name
      title
      contentHtml
      details {
        id
        detailType
        isExample
        options {
          id
          correctAnswer
          text
          image {
            id
            url
          }
          label
        }
        explainHtml
        explainHtmlEn
        contentKey
      }
    }
  }
`);

export const IeltsReadingQuestionFragment = graphql(/* GraphQL */ `
  fragment IeltsReadingQuestion on IeltsReadingPartQuestionEdge {
    position
    node {
      id
      ieltsReadingQuestionId
      name
      title
      contentHtml
      details {
        id
        detailType
        isExample
        options {
          id
          correctAnswer
          text
          image {
            id
            url
          }
          label
        }
        explainHtml
        explainHtmlEn
        contentKey
      }
    }
  }
`);

export const IeltsWritingQuestionFragment = graphql(/* GraphQL */ `
  fragment IeltsWritingQuestion on IeltsWritingPartQuestionEdge {
    position
    node {
      id
      ieltsWritingQuestionId
      name
      title
      contentHtml
      details {
        id
        detailType
        isExample
        options {
          id
          correctAnswer
          text
          image {
            id
            url
          }
          label
        }
        explainHtml
        explainHtmlEn
        contentKey
      }
    }
  }
`);

export const ViewerXpSummaries = graphql(`
  query ViewerXpSummaries(
    $first: Int
    $after: Cursor
    $last: Int
    $before: Cursor
    $filterBy: LearnerXpFilter
  ) {
    viewerXpSummaries(
      first: $first
      after: $after
      last: $last
      before: $before
      filterBy: $filterBy
    ) {
      nodes {
        date
        id
        learnerXpId
      }
    }
  }
`);

export const GetPlayerInfoChallengeByCodeQuery = graphql(`
  query GetPlayersInfoChallengeByCode($code: String!) {
    challengeByCode(code: $code) {
      id: challengeId
      code
      learnerProfile {
        id
        learnerProfileId
        displayName
      }
      players(first: 250) {
        totalCount
        edges {
          node {
            id
            challengePlayerId
            joinedAt
            updatedAt
            learnerProfile {
              id: learnerProfileId
              learnerProfileId
              displayName
              avatarUrl
              frameImage {
                id
                url
              }
            }
            attempt {
              id
              grade
              totalScore
            }
          }
        }
      }
    }
  }
`);

export const GetViewerAchievements = graphql(`
  query ViewerAchievements {
    viewerAchievements {
      id
      learnerAchievementId
      level
      name
      seen
      tier
      value
      dateReceived
      nextLevelValue
      maxLevel
    }
  }
`);

export const ViewerUpdateSeenAchievement = graphql(`
  mutation ViewerSeenAchievements($inputs: [ViewerSeenAchievementInput!]!) {
    viewerSeenAchievements(inputs: $inputs) {
      userErrors {
        code
        field
      }
    }
  }
`);

export const IeltsListeningCheckPointsQuery = graphql(`
  query IeltsListeningCheckPoints($checkPointId: ID!) {
    checkPoint(id: $checkPointId) {
      id
      checkPointId
      name
      nameEn
      description
      descriptionEn
      diamond
      cpType
      type
      viewerCanAccess
      time
      theme {
        id
        name
        nameEn
        key
      }
      checkPointIeltsListeningParts {
        position
        node {
          id
          expiredTime
          audioSubtitle
          audio {
            id
            url
          }
          ieltsListeningPartId
          name
          order
          title
          ieltsListeningPartQuestions {
            ...IeltsListeningQuestion
          }
        }
      }
    }
  }
`);

export const IeltsReadingCheckPointsQuery = graphql(`
  query IeltsReadingCheckPoints($checkPointId: ID!) {
    checkPoint(id: $checkPointId) {
      id
      checkPointId
      name
      nameEn
      description
      descriptionEn
      diamond
      cpType
      type
      viewerCanAccess
      time
      theme {
        id
        name
        nameEn
        key
      }
      checkPointIeltsReadingParts {
        position
        node {
          id
          expiredTime
          contentHtml
          ieltsReadingPartId
          name
          order
          title
          ieltsReadingPartQuestions {
            ...IeltsReadingQuestion
          }
        }
      }
    }
  }
`);

export const IeltsListeningSubmitPartAttemptMutation = graphql(`
  mutation IeltsListeningSubmitPartAttempt(
    $input: IeltsListeningSubmitPartAttemptInput!
  ) {
    ieltsListeningSubmitPartAttempt(input: $input) {
      userErrors {
        message
        code
      }
    }
  }
`);
export const IeltsWritingSubmitPartAttemptMutation = graphql(`
  mutation IeltsWritingSubmitPartAttempt(
    $input: IeltsWritingSubmitPartAttemptInput!
  ) {
    ieltsWritingSubmitPartAttempt(input: $input) {
      userErrors {
        code
        message
      }
    }
  }
`);

export const IeltsReadingSubmitPartAttemptMutation = graphql(`
  mutation IeltsReadingSubmitPartAttempt(
    $input: IeltsReadingSubmitPartAttemptInput!
  ) {
    ieltsReadingSubmitPartAttempt(input: $input) {
      userErrors {
        message
        code
      }
    }
  }
`);

export const IeltsListeningEndAttemptMutation = graphql(`
  mutation IeltsListeningEndAttempt($input: IeltsListeningEndAttemptInput!) {
    ieltsListeningEndAttempt(input: $input) {
      userErrors {
        message
        code
      }
    }
  }
`);

export const IeltsReadingEndAttemptMutation = graphql(`
  mutation IeltsReadingEndAttempt($input: IeltsReadingEndAttemptInput!) {
    ieltsReadingEndAttempt(input: $input) {
      userErrors {
        message
        code
      }
    }
  }
`);
export const IeltsWritingEndAttemptMutation = graphql(`
  mutation IeltsWritingEndAttempt($input: IeltsWritingEndAttemptInput!) {
    ieltsWritingEndAttempt(input: $input) {
      userErrors {
        code
        message
      }
    }
  }
`);

export const NoteFragment = graphql(/* GraphQL */ `
  fragment IeltsNote on IeltsNote {
    id
    ieltsNoteId
    text
    description
    learnerProfile {
      id
      displayName
      learnerProfileId
    }
    readingPartId
    listeningQuestionId
    listeningPartId
    readingQuestionId
    checkPoint {
      id
      checkPointId
    }
    vocabWord {
      id
      level
      text
      vocabWordId
      phonetics
    }
  }
`);

export const CreateNote = graphql(`
  mutation IeltsViewerNoteCreate($input: IeltsViewerNoteCreateInput!) {
    ieltsViewerNoteCreate(input: $input) {
      ieltsNote {
        ...IeltsNote
      }
    }
  }
`);

export const RemoveNote = graphql(`
  mutation IeltsViewerNoteRemove($ieltsViewerNoteRemoveId: ID!) {
    ieltsViewerNoteRemove(id: $ieltsViewerNoteRemoveId) {
      ieltsNoteId
    }
  }
`);

export const UpdateNote = graphql(`
  mutation IeltsViewerNoteUpdate($input: IeltsViewerNoteUpdateInput!) {
    ieltsViewerNoteUpdate(input: $input) {
      ieltsNote {
        id
        text
        description
      }
    }
  }
`);

export const ListNoteCheckPoint = graphql(`
  query IeltsViewerNotes(
    $first: Int
    $after: Cursor
    $last: Int
    $before: Cursor
    $filter: IeltsNoteFilter
  ) {
    ieltsViewerNotes(
      first: $first
      after: $after
      last: $last
      before: $before
      filter: $filter
    ) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      edges {
        cursor
        node {
          ...IeltsNote
        }
      }
    }
  }
`);

export const CheckPointsIeltsWithNotes = graphql(`
  query CheckPointsIeltsWithNotes(
    $first: Int
    $ieltsNotesFirst2: Int
    $last: Int
    $after: Cursor
    $before: Cursor
    $filter2: IeltsNoteFilter
    $filterBy: CheckPointFilter
    $includedPractice: Boolean!
  ) {
    ieltsCheckPoints(
      first: $first
      filterBy: $filterBy
      includedPractice: $includedPractice
    ) {
      edges {
        cursor
        node {
          checkPointId
          id
          skills
          name
          nameEn
          ieltsNotes(
            first: $ieltsNotesFirst2
            last: $last
            after: $after
            before: $before
            filterBy: $filter2
          ) {
            edges {
              cursor
              node {
                id
                ...IeltsNote
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        hasPreviousPage
        startCursor
      }
    }
  }
`);
export const GetAudio = graphql(`
  query getAudioUrlFromText($input: TextToSpeakInput) {
    textToSpeak(input: $input) {
      url
      userErrors {
        code
      }
    }
  }
`);

export const QueryListWordsIelts = graphql(`
  query ViewerMistakeWordsIelts(
    $after: Cursor
    $before: Cursor
    $first: Int
    $last: Int
    $filterBy: LearnerMistakeWordFilter
  ) {
    viewerMistakeWords(
      after: $after
      before: $before
      first: $first
      last: $last
      filterBy: $filterBy
    ) {
      edges {
        node {
          id
          key: id
          isLastScore
          recordScore {
            audioUrl
            decision
            endIndex
            ipa
            nativenessScore
            text
            decision
            phonemes {
              text
              nativenessScore
              decision
              ipa
            }
          }
          learnerMistakeWordId
          recordUrl
          word {
            id
            phonetics
            text
            vocabWordId
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      totalCount
    }
  }
`);

export const GetTotalRequest = graphql(`
  query ViewerScoreMistakeWordQuota {
    viewerScoreMistakeWordQuota {
      total
      used
      remaining
    }
  }
`);
export const ViewerMistakeWordIeltsUpdateMutation = graphql(/* GraphQL */ `
  mutation ViewerMistakeWordIeltsUpdate($input: ViewerMistakeWordUpdateInput!) {
    viewerMistakeWordUpdate(input: $input) {
      learnerMistakeWord {
        id
        isLastScore
        recordScore {
          audioUrl
          decision
          endIndex
          ipa
          nativenessScore
          text
          decision
          phonemes {
            text
            nativenessScore
            decision
            ipa
          }
        }
        learnerMistakeWordId
        recordUrl
        word {
          id
          phonetics
          text
          vocabWordId
        }
      }
      userErrors {
        code
        message
      }
    }
  }
`);

export const IeltsListeningListItem = graphql(`
  fragment IeltsListeningListItem on CheckPoint {
    id
    name
    nameEn
    description
    descriptionEn
    imageUrl
    image {
      url
      id
    }
    type
    createdAt
    isPremium
    testCount
    skills
    ieltsQuestionDetailTypes
    ieltsListeningParts {
      id
      order
      ieltsListeningPartQuestions {
        node {
          id
          details {
            id
            detailType
            options {
              id
              correctAnswer
            }
          }
        }
      }
    }
    isHot
    viewerCanAccess
    viewerCompleted
    viewerIeltsStar
    isBoss
    position
    checkPointId
    time
    star
    diamond
    topics {
      id
      name
      nameEn
      topicId
    }
    viewerLatestAttempt {
      grade
      maxGrade
      teacherGrade
      totalScore
      id
    }
  }
`);

export const IeltsReadingListItem = graphql(`
  fragment IeltsReadingListItem on CheckPoint {
    id
    name
    nameEn
    description
    descriptionEn
    imageUrl
    image {
      url
      id
    }
    type
    createdAt
    isPremium
    testCount
    ieltsQuestionDetailTypes
    ieltsReadingParts {
      id
      order
      ieltsReadingPartQuestions {
        node {
          id
          details {
            id
            detailType
            options {
              id
              correctAnswer
            }
          }
        }
      }
    }
    isHot
    viewerCanAccess
    viewerCompleted
    viewerIeltsStar
    isBoss
    position
    checkPointId
    time
    star
    diamond
    topics {
      id
      name
      nameEn
      topicId
    }
    viewerLatestAttempt {
      grade
      maxGrade
      teacherGrade
      totalScore
      id
    }
  }
`);

export const IeltsSpeakingListItem = graphql(`
  fragment IeltsSpeakingListItem on CheckPoint {
    id: checkPointId
    name
    nameEn
    time
    checkPointIeltsSpeakingParts {
      node {
        id
        order
        ieltsSpeakingPartQuestions {
          node {
            id
          }
        }
      }
    }
    viewerCanAccess
    viewerCompleted
    viewerIeltsStar
    isBoss
    position
  }
`);

export const IeltsWritingListItem = graphql(`
  fragment IeltsWritingListItem on CheckPoint {
    id: checkPointId
    name
    nameEn
    time
    checkPointIeltsWritingParts {
      position
      node {
        id
        order
        ieltsWritingPartId
        name
        title
      }
    }
    viewerCanAccess
    viewerCompleted
    viewerIeltsStar
    position
    isBoss
  }
`);
graphql(`
  query GetInfoCheckpointIeltsDetailSEO($id: ID!) {
    checkPoint(id: $id) {
      imageUrl
      name
      nameEn
      description
      descriptionEn
      testLevel {
        id
        slug
        name
      }
    }
  }
`);

export const ViewerAiConversationSessionDetail = graphql(`
  query ViewerAiConversationSessionDetail(
    $aiConversationSessionId: ID!
    $first: Int
    $after: Cursor
    $last: Int
    $before: Cursor
    $filterBy: AIConversationFilter
    $orderBy: AIConversationOrder
  ) {
    viewerAiConversationSession(
      aiConversationSessionId: $aiConversationSessionId
    ) {
      aiConversationSessionId
      id
      createdAt
      learnerProfile {
        displayName
        id
      }
      messageCount
      aiConversations(
        first: $first
        after: $after
        last: $last
        before: $before
        filterBy: $filterBy
        orderBy: $orderBy
      ) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
          hasPreviousPage
          startCursor
        }
        edges {
          node {
            aiConversationId
            id
            message
            updatedAt
            role
          }
          cursor
        }
      }
    }
  }
`);

export const fragmentStandardFeedback = graphql(`
  fragment StandardFeedback on StandardFeedback {
    feedback
    improve
    feedbackVi
    improveVi
    evaluationKey
  }
  fragment OverallSummary on OverallSummary {
    score
    summary
    summaryVi
  }
`);

export const IeltsWritingUserAttempt = graphql(`
  query IeltsWritingUserAttempt($userAttemptId: ID!) {
    userAttempt(id: $userAttemptId) {
      id
      assignTest {
        isHiddenAnswer
        assignTestId
      }
      createdAt
      ieltsWritingPartAttempts {
        id
        answer
        markingGrade
        detailedFeedback {
          lexicalResourceBreakdown {
            sentenceSuggestion {
              index
              originalSentence
              suggestedText
              textChanges {
                originalText
                suggestedText
                errorImpact
                category
                analysis
              }
            }
            rangeFlexibilityPrecision {
              ...StandardFeedback
            }
            appropriacyAndCollocation {
              ...StandardFeedback
            }
            spellingAndWordFormation {
              ...StandardFeedback
            }
          }
          grammaticalRangeAndAccuracyBreakdown {
            sentenceSuggestion {
              index
              originalSentence
              suggestedText
              textChanges {
                originalText
                suggestedText
                analysis
                category
                errorImpact
              }
            }
            rangeAndFlexibility {
              ...StandardFeedback
            }
            accuracy {
              ...StandardFeedback
            }
            punctuationControl {
              ...StandardFeedback
            }
          }
          taskAchievementBreakdown {
            coversAllParts {
              ...StandardFeedback
            }
            selectsKeyFeatures {
              ...StandardFeedback
            }
            providesClearOverview {
              ...StandardFeedback
            }
            appropriateFormat {
              ...StandardFeedback
            }
            dataAndSupport {
              ...StandardFeedback
            }
          }
          coherenceAndCohesionBreakdown {
            clarityAndProgression {
              ...StandardFeedback
            }
            paragraphing {
              ...StandardFeedback
            }
            useOfCohesiveDevices {
              ...StandardFeedback
            }
            referenceAndSubstitution {
              ...StandardFeedback
            }
          }
          overallScore {
            overallBand
            taskPerformance {
              ...OverallSummary
            }
            coherenceAndCohesion {
              ...OverallSummary
            }
            lexicalResource {
              ...OverallSummary
            }
            grammaticalRangeAndAccuracy {
              ...OverallSummary
            }
          }
          taskResponseBreakdown {
            appropriateFormat {
              ...StandardFeedback
            }
            clearPosition {
              ...StandardFeedback
            }
            ideaDevelopment {
              ...StandardFeedback
            }
            taskFulfilment {
              ...StandardFeedback
            }
          }
        }
        questionAttempts {
          id
          correctMark
          ieltsWritingQuestionAttemptId
          detailAnswers {
            text
            optionIds
            optionId
            detailId
            correctOptionIDs
          }
          question {
            id
            ieltsWritingQuestionId
          }
        }
        ieltsWritingPartAttemptId
        marking {
          criteria {
            commentary
            detailCommentary
            criterion
            detailScores {
              criterion
              score
            }
            score
          }
        }
        teacherCommentsAnswer {
          id
          content
          quote
        }
        teacherFixedAnswer
        markingCriteria {
          criteria
          band
          commentary
        }
        part {
          id
          ieltsWritingPartId
          expiredTime
          contentHtml
          images {
            id
            label
            url
          }
          title
          sampleAnswerHtml
          order
        }
      }
      checkPoint {
        id
        checkPointId
        name
        nameEn
        time
        type
        checkPointIeltsWritingParts {
          position
          node {
            id
            ieltsWritingPartId
            sampleAnswerHtml
            order
            title
            expiredTime
            contentHtml
            learnFromSampleHtml
            sampleAnswerHtml
            images {
              id
              url
            }
            ieltsWritingPartQuestions {
              ...IeltsWritingQuestion
            }
          }
        }
      }
      testLevel {
        id
        name
        slug
      }
      grade
      maxGrade
      teacherGrade
      teacherComment
      submittedAt
      learnerProfile {
        id
        learnerProfileId
        avatarUrl
        displayName
        frameImage {
          id
          url
        }
        classStudent {
          classID
          class {
            id
            name
          }
        }
      }
      executionTime
      requestReview
      userAttemptId
    }
  }
`);

export const BingoSubjectFragment = graphql(`
  fragment BingoSubjectFragment on BingoSubject {
    id
    bingoSubjectId
    imageId
    topic {
      id
      topicId
      name
      nameEn
    }
    enabled
    description
    descriptionEn
    createdAt
    promptAi
    title
    titleEn
    level
    studentCount
    learningOutcomeSubjects {
      id
      learningOutcomeSubjectId
      title
      titleEn
      bingoSubject {
        id
        imageId
      }
    }
  }
`);

export const ViewerBingoSubjects = graphql(`
  query ViewerBingoSubjects(
    $first: Int
    $after: Cursor
    $last: Int
    $before: Cursor
    $filterBy: BingoSubjectFilter
    $aiConversationSessionsFirst2: Int
    $aiConversationSessionsFilterBy2: AIConversationSessionFilter
  ) {
    viewerBingoSubjects(
      first: $first
      after: $after
      last: $last
      before: $before
      filterBy: $filterBy
    ) {
      edges {
        node {
          ...BingoSubjectFragment
          aiConversationSessions(
            first: $aiConversationSessionsFirst2
            filterBy: $aiConversationSessionsFilterBy2
          ) {
            totalCount
            nodes {
              id
              aiConversationSessionId
              isCompleted
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`);

export const ViewerBingoSubject = graphql(`
  query ViewerBingoSubject(
    $viewerBingoSubjectId: ID!
    $filterBy: AIConversationSessionFilter
  ) {
    viewerBingoSubject(id: $viewerBingoSubjectId) {
      ...BingoSubjectFragment
      aiConversationSessions(first: 1, filterBy: $filterBy) {
        totalCount
        nodes {
          id
          aiConversationSessionId
          isCompleted
        }
      }
    }
  }
`);

export const ViewerAIConversationSessionCreateMutation = graphql(`
  mutation ViewerAIConversationSessionCreate(
    $input: ViewerAIConversationSessionCreateInput!
  ) {
    viewerAIConversationSessionCreate(input: $input) {
      sessionId
      userErrors {
        code
        message
      }
    }
  }
`);

export const ViewerBingoSubjectSuggestionsMutation = graphql(`
  mutation ViewerBingoSubjectSuggestions(
    $input: ViewerBingoSubjectSuggestionsInput!
  ) {
    viewerBingoSubjectSuggestions(input: $input)
  }
`);

export const ConversationTextToSpeak = graphql(`
  query ConversationTextToSpeak($input: TextToSpeakInput) {
    textToSpeak(input: $input) {
      url
      userErrors {
        code
        message
      }
    }
  }
`);

export const ViewerAIConversationUpdateMutation = graphql(`
  mutation ViewerAIConversationUpdate(
    $input: ViewerAIConversationUpdateInput!
  ) {
    viewerAIConversationUpdate(input: $input) {
      aiConversation {
        id
      }
      userErrors {
        code
        message
      }
    }
  }
`);

export const ViewerAIConversationCompleteMutation = graphql(`
  mutation ViewerAIConversationComplete(
    $input: ViewerAIConversationCompleteInput!
  ) {
    viewerAIConversationComplete(input: $input) {
      systemText
      systemAudio
      userMessageId
      visemes {
        endTime
        phoneme
        startTime
        types
      }
      userErrors {
        code
        message
      }
    }
  }
`);

export const ViewerAIConversationCompleteTopicMutation = graphql(`
  mutation ViewerAIConversationCompleteTopic(
    $input: ViewerAIConversationCompleteInput!
  ) {
    viewerAIConversationComplete(input: $input) {
      systemAudio
      systemText
      userMessageId
      aiConversationSession {
        id
        aiConversationSessionId
        isCompleted
        conversationSessionSubjectDone {
          id
          conversationSessionSubjectDoneId
          learningOutcomeSubject {
            id
            learningOutcomeSubjectId
          }
        }
      }
      userErrors {
        code
        message
      }
    }
  }
`);

export const ViewerAiConversationSessionBingoSubject = graphql(`
  query ViewerAiConversationSessionBingoSubject(
    $aiConversationSessionId: ID!
    $filterBy: AIConversationFilter
    $first: Int
    $after: Cursor
    $orderBy: AIConversationOrder
  ) {
    viewerAiConversationSession(
      aiConversationSessionId: $aiConversationSessionId
    ) {
      id
      aiConversationSessionId
      bingoSubject {
        ...BingoSubjectFragment
      }
      isCompleted
      conversationSessionSubjectDone {
        id
        conversationSessionSubjectDoneId
        learningOutcomeSubject {
          id
          learningOutcomeSubjectId
        }
      }
      aiConversations(
        filterBy: $filterBy
        first: $first
        after: $after
        orderBy: $orderBy
      ) {
        nodes {
          id
          aiConversationId
          message
          role
          createdAt
          audioAWS {
            id
            audioAWSId
            url
          }
          bingoImage {
            id
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`);

export const ViewerAiConversationSessionCheckOutcome = graphql(`
  query ViewerAiConversationSessionCheckOutcome($aiConversationSessionId: ID!) {
    viewerAiConversationSession(
      aiConversationSessionId: $aiConversationSessionId
    ) {
      id
      aiConversationSessionId
      isCompleted
      bingoSubject {
        id
        learningOutcomeSubjects {
          id
          learningOutcomeSubjectId
        }
      }
      conversationSessionSubjectDone {
        id
        conversationSessionSubjectDoneId
        learningOutcomeSubject {
          id
          learningOutcomeSubjectId
        }
      }
    }
  }
`);

export const ViewerAIConversationSessionUpdateMutation = graphql(`
  mutation ViewerAIConversationSessionUpdate(
    $input: ViewerAIConversationSessionUpdateInput!
  ) {
    viewerAIConversationSessionUpdate(input: $input) {
      sessionId
      userErrors {
        code
        message
      }
    }
  }
`);
export const ViewerAIConversationSessionCreateChatMutation = graphql(`
  mutation ViewerAIConversationSessionCreate(
    $input: ViewerAIConversationSessionCreateInput!
  ) {
    viewerAIConversationSessionCreate(input: $input) {
      sessionId
      userErrors {
        code
        message
      }
    }
  }
`);

export const IeltsProvincesGet = graphql(`
  query getProvinces {
    provinces: provinces {
      id
      name
      provinceId
    }
  }
`);

export const ViewerExternalAssignedTestMarkCompletedMutation = graphql(`
  mutation ViewerExternalAssignedTestMarkCompleted($assignedTestId: ID!) {
    viewerExternalAssignedTestMarkCompleted(assignedTestId: $assignedTestId) {
      userErrors {
        code
        message
      }
    }
  }
`);

